
import RegistrationService from '@/services/RegistrationService';
import {
  computed, defineComponent, onMounted, reactive, ref, watch
} from 'vue';
import LRPopup from '@/components/login-register/LRPopup.vue';
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import BCXValidatedInput from '@/components/molecules/BCXValidatedInput.vue';
import useBCValidators from '@/mixins/useBCValidators';
import useVuelidate from '@vuelidate/core';
import { useRoute, useRouter } from 'vue2-helpers/vue-router';
import { useStore } from 'vue2-helpers/vuex';
import ButtonBlock from '@/components/login-register/ButtonBlock.vue';
import BackendErrors from '@/components/login-register/BackendErrors.vue';
import useI18n from '@/mixins/useI18n';
import { Lang, UserType } from '@/models/User';
import useUser from '@/mixins/useUser';
import {
  asyncComputed, useLocalStorage, useSessionStorage, useTimeout
} from '@vueuse/core';
import { REGISTRATION_RESEND_EMAIL_INTERVAL } from '@/models/ResendTimout';

export default defineComponent({
  name: 'Verification',
  components: {
    BackendErrors,
    ButtonBlock,
    BCXValidatedInput,
    BCXMarkdown,
    LRPopup
  },
  setup() {
    const resent = ref(false);
    const resending = ref(false);
    const resendError = ref(false);
    const submitting = ref(false);
    const showConfirm = ref(false);
    const emptyCode = ref(true);
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const backendErrors = ref([]);
    const guestInviteId = useLocalStorage('guestInviteId', '');
    const { ready: canResend, start: startResendInterval } = useTimeout(REGISTRATION_RESEND_EMAIL_INTERVAL, { controls: true });

    const routeFlow = computed(() => {
      const value = route?.query?.flow;
      return Array.isArray(value) ? value[0] : value;
    });
    const routeInviteId = computed(() => {
      const value = route?.query?.inviteId;
      return Array.isArray(value) ? value[0] : value;
    });

    const pid = computed(() => (route?.query?.pid as string) || store.getters.registrationProcessId || '');

    const user = asyncComputed(async () => {
      if (pid.value) {
        const registeringUser = await RegistrationService.getRegisteringUser(pid.value);
        store.commit('setUser', registeringUser);
        return registeringUser;
      }
      return store.getters.user;
    }, null);

    const email = computed(() => user.value?.email ?? '');

    // Hand the email to RegistrationFinished, which pre-fills the login form but
    // has no pid left to fetch it. Cleared once consumed.
    const registrationEmail = useSessionStorage('registrationEmail', '');
    watch(email, (value) => {
      if (value) registrationEmail.value = value;
    });

    const { locale } = useI18n();

    const { tUserFallback: t, type: userType } = useUser(user);
    const normalizeFlow = (value: unknown): UserType | null => {
      if (value === 'freelancer' || value === UserType.FREELANCER) return UserType.FREELANCER;
      if (value === 'company' || value === UserType.COMPANY) return UserType.COMPANY;
      if (value === 'guest' || value === UserType.GUEST) return UserType.GUEST;
      return null;
    };
    const effectiveFlow = computed(() => normalizeFlow(routeFlow.value) ?? userType.value);
    const effectiveGuestInviteId = computed(() => routeInviteId.value || route.params?.inviteId || guestInviteId.value || '');

    const userId = computed(() => user.value?.userId ?? '');

    const { passwordValidation, required } = useBCValidators();

    const fields = reactive({
      code: route?.query.code ?? '',
      password: ''
    });

    const rules = {
      code: { required },
      password: passwordValidation
    };

    const v = useVuelidate(rules, fields);

    const resendEmail = async () => {
      resending.value = true;
      await RegistrationService.resendEmail(userId.value, pid.value as string)
        .catch(() => {
          resendError.value = true;
        })
        .finally(() => {
          resent.value = true;
          setTimeout(() => {
            resending.value = false;
          }, 10000);
        });
    };

    const submit = async () => {
      if (!submitting.value && (await v.value.$validate())) {
        const params = {
          verificationCode: fields.code,
          password: fields.password,
          registrationProcessId: pid.value,
          userId: userId.value
        };

        submitting.value = true;
        const backendErrorObject = await store.dispatch('submitVerificationData', params);

        backendErrors.value = backendErrorObject.allErrors;

        submitting.value = false;
      }
    };

    const showBackButton = computed(() => userType.value != null && emptyCode.value);

    const goBack = () => {
      if (effectiveFlow.value === UserType.COMPANY) {
        router.push({ name: 'register-customer' });
        return;
      }
      if (effectiveFlow.value === UserType.GUEST) {
        if (effectiveGuestInviteId.value) {
          router.push({ name: 'register-guest', params: { inviteId: effectiveGuestInviteId.value } });
          return;
        }
        router.push({ name: 'register' });
        return;
      }
      router.push({ name: 'register' });
    };

    const resend = () => {
      if (resending.value || !canResend.value) return;
      if (effectiveFlow.value === UserType.GUEST) {
        startResendInterval();
        resendEmail();
      } else {
        showConfirm.value = true;
      }
    };

    const onConfirmed = (is: boolean) => {
      if (is) {
        resendEmail();
      } else {
        goBack();
      }
    };

    onMounted(() => {
      emptyCode.value = !route.query?.code?.length;
      if (effectiveGuestInviteId.value) {
        guestInviteId.value = effectiveGuestInviteId.value;
      }
      if (route.query.locale) {
        const queryLocale = route.query?.locale as string;
        const [localeValue] = queryLocale.split('-');
        if (Object.keys(Lang).includes(localeValue)) {
          locale.value = localeValue;
        }
      }
    });

    return {
      showConfirm,
      fields,
      v,
      resend,
      goBack,
      onConfirmed,
      submit,
      resent,
      resending,
      resendError,
      backendErrors,
      emptyCode,
      t,
      showBackButton,
      email,
      canResend
    };
  }
});
